<script setup>
import mainLogo from "@images/brand/alomonshi-symbol.png"

const emit = defineEmits(["loginSuccess", "authSuccess", "showLegal"])
const { isRequired, isValidMobile } = useValidator()
const { panelLogin, passwordLogin, passwordRegister } = useRequest()
const session = useAuthSession()

const { passwordLoginEnabled, refreshAuthenticationOptions } = useAuthenticationOptions()
const { isDialogVisible } = useAuthDialog()
watch(isDialogVisible, visible => { if (visible) refreshAuthenticationOptions() })
const mode = ref("otp")
const mobileNumber = ref("")
const firstName = ref("")
const password = ref("")
const passwordConfirmation = ref("")
const passwordVisible = ref(false)
const confirmationVisible = ref(false)
const loading = ref(false)
const formRef = ref(null)

const title = computed(() => mode.value === "register" ? "ساخت حساب کاربری" : "خوش آمدی")
const description = computed(() => {
  if (mode.value === "password") return "با شماره موبایل و رمز عبور وارد حساب خود شوید."
  if (mode.value === "register") return "اطلاعات زیر را وارد کنید تا حساب شما ساخته شود."
  return "شماره موبایل خود را وارد کنید تا کد تأیید برایتان ارسال شود."
})

const normalizeMobileNumber = value => String(value ?? "")
  .replace(/[۰-۹]/g, digit => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit))
  .replace(/[٠-٩]/g, digit => "٠١٢٣٤٥٦٧٨٩".indexOf(digit))
  .replace(/\D/g, "")
  .slice(0, 11)

const updateMobileNumber = value => {
  mobileNumber.value = normalizeMobileNumber(value)
}

const normalizePassword = value => String(value ?? "")
  .replace(/[۰-۹]/g, digit => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit))
  .replace(/[٠-٩]/g, digit => "٠١٢٣٤٥٦٧٨٩".indexOf(digit))
  .replace(/\D/g, "")
  .slice(0, 72)

const validateTemporaryPassword = value => /^\d{9,72}$/.test(String(value ?? "")) || "رمز عبور باید بین ۹ تا ۷۲ رقم باشد"

const storeAuthentication = authData => {
  session.save(authData)
}

const changeMode = nextMode => {
  if (nextMode !== "otp" && !passwordLoginEnabled.value) return
  mode.value = nextMode
  password.value = ""
  passwordConfirmation.value = ""
  formRef.value?.resetValidation()
}

const submitForm = async () => {
  if (loading.value) return
  if (mode.value !== "otp" && !passwordLoginEnabled.value) { changeMode("otp"); return }
  const validation = await formRef.value?.validate()
  if (!validation?.valid) return

  if (mode.value === "register" && password.value !== passwordConfirmation.value) {
    toast({ message: "رمز عبور و تکرار آن یکسان نیستند.", type: "info" })
    return
  }

  loading.value = true
  try {
    if (mode.value === "otp") {
      const response = await panelLogin({ body: { mobileNumber: mobileNumber.value } })
      if (!response || response.error?.value) {
        if (passwordLoginEnabled.value && Number(response?.error?.value?.data?.responseCode) === -2007) changeMode("password")
        return
      }
      useCookie("mobileNumber").value = mobileNumber.value
      emit("loginSuccess", mobileNumber.value)
      return
    }

    const request = mode.value === "register" ? passwordRegister : passwordLogin
    const body = mode.value === "register"
      ? {
          mobileNumber: mobileNumber.value,
          firstName: firstName.value.trim(),
          password: password.value,
          passwordConfirmation: passwordConfirmation.value,
        }
      : { mobileNumber: mobileNumber.value, password: password.value }
    const response = await request({ body })
    if (Number(response?.error?.value?.data?.responseCode) === -2009) {
      await refreshAuthenticationOptions()
      changeMode("otp")
      return
    }
    const authData = response?.data?.value
    if (!authData?.token || response.error?.value) return

    storeAuthentication(authData)
    emit("authSuccess")
  }
  finally {
    loading.value = false
  }
}
watch(passwordLoginEnabled, enabled => {
  if (!enabled && mode.value !== "otp") changeMode("otp")
})
</script>

<template>
  <VCard class="login-step" flat>
    <div class="login-step__brand"><img :src="mainLogo" alt="الومنشی"></div>

    <header>
      <span>{{ mode === "register" ? "ثبت‌نام" : "ورود به الومنشی" }}</span>
      <h2>{{ title }}</h2>
      <p>{{ description }}</p>
    </header>

    <div v-if="passwordLoginEnabled && mode !== 'register'" class="login-step__switch" role="tablist" aria-label="روش ورود">
      <button type="button" :class="{ active: mode === 'otp' }" @click="changeMode('otp')">رمز یکبار مصرف</button>
      <button type="button" :class="{ active: mode === 'password' }" @click="changeMode('password')">رمز عبور</button>
    </div>

    <VForm ref="formRef" :class="['login-step__form', { 'login-step__form--register': mode === 'register' }]" @submit.prevent="submitForm">
      <VTextField
        v-if="mode === 'register'"
        v-model.trim="firstName"
        label="نام"
        autocomplete="name"
        prepend-inner-icon="tabler-user"
        variant="outlined"
        :disabled="loading"
        :rules="[isRequired]"
      />

      <VTextField
        :model-value="mobileNumber"
        label="شماره موبایل"
        type="tel"
        inputmode="numeric"
        pattern="[0-9]*"
        autocomplete="tel-national"
        maxlength="11"
        dir="ltr"
        placeholder="09123456789"
        prepend-inner-icon="tabler-phone"
        variant="outlined"
        :disabled="loading"
        :rules="[isRequired, isValidMobile]"
        @update:model-value="updateMobileNumber"
      />

      <VTextField
        v-if="mode !== 'otp'"
        :model-value="password"
        label="رمز عبور"
        :type="passwordVisible ? 'text' : 'password'"
        autocomplete="new-password"
        name="alomonshi-temporary-password"
        data-1p-ignore
        data-lpignore="true"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="72"
        dir="ltr"
        prepend-inner-icon="tabler-lock"
        :append-inner-icon="passwordVisible ? 'tabler-eye-off' : 'tabler-eye'"
        variant="outlined"
        :disabled="loading"
        :rules="mode === 'register' ? [isRequired, validateTemporaryPassword] : [isRequired]"
        @update:model-value="password = normalizePassword($event)"
        @click:append-inner="passwordVisible = !passwordVisible"
      />

      <VTextField
        v-if="mode === 'register'"
        :model-value="passwordConfirmation"
        label="تکرار رمز عبور"
        :type="confirmationVisible ? 'text' : 'password'"
        autocomplete="new-password"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="72"
        dir="ltr"
        prepend-inner-icon="tabler-lock-check"
        :append-inner-icon="confirmationVisible ? 'tabler-eye-off' : 'tabler-eye'"
        variant="outlined"
        :disabled="loading"
        :rules="[isRequired, value => value === password || 'تکرار رمز عبور یکسان نیست']"
        @update:model-value="passwordConfirmation = normalizePassword($event)"
        @click:append-inner="confirmationVisible = !confirmationVisible"
      />

      <VBtn block type="submit" color="primary" size="large" rounded="lg" :loading="loading" :disabled="loading || mobileNumber.length !== 11" append-icon="tabler-arrow-left">
        {{ mode === "otp" ? "دریافت کد تأیید" : mode === "register" ? "ثبت‌نام و ورود" : "ورود با رمز عبور" }}
      </VBtn>
    </VForm>

    <div v-if="passwordLoginEnabled" class="login-step__alternate">
      <template v-if="mode === 'register'">قبلاً ثبت‌نام کرده‌اید؟ <button type="button" @click="changeMode('password')">ورود به حساب</button></template>
      <template v-else>حساب کاربری ندارید؟ <button type="button" @click="changeMode('register')">ثبت‌نام با رمز عبور</button></template>
    </div>

    <p class="login-step__terms">
      با ورود به الومنشی، <button type="button" @click="emit('showLegal', 'terms')">شرایط و مقررات</button>
      و <button type="button" @click="emit('showLegal', 'privacy')">حریم خصوصی</button> را می‌پذیرید.
    </p>

    <div class="login-step__security"><VIcon icon="tabler-shield-check" size="18" />اطلاعات شما نزد الومنشی محفوظ است.</div>
  </VCard>
</template>

<style lang="scss" scoped>
.login-step { width: min(410px,calc(100vw - 28px)); padding: 28px 25px 23px; border: 1px solid rgba(var(--v-theme-primary),.1); border-radius: 20px; background: #fff; color: rgba(var(--v-theme-on-surface),.88); box-shadow: 0 18px 50px rgba(42,34,78,.12); }
.login-step__brand { display: grid; width: 62px; height: 62px; margin: 0 auto 16px; place-items: center; border-radius: 18px; background: linear-gradient(145deg,rgba(var(--v-theme-primary),.05),rgba(var(--v-theme-primary),.13)); }
.login-step__brand img { width: 48px; height: 48px; object-fit: contain; }
.login-step header { text-align: center; }
.login-step header > span { color: rgb(var(--v-theme-primary)); font-size: 10px; font-weight: 700; }
.login-step h2 { margin: 7px 0 5px; color: #302a42; font-size: 21px; }
.login-step header p { max-width: 310px; margin: 0 auto; color: rgba(var(--v-theme-on-surface),.53); font-size: 10px; line-height: 2; }
.login-step__switch { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; margin-top: 18px; padding: 4px; border-radius: 12px; background: rgba(var(--v-theme-primary),.055); }
.login-step__switch button { min-height: 34px; border: 0; border-radius: 9px; background: transparent; color: rgba(var(--v-theme-on-surface),.55); cursor: pointer; font: inherit; font-size: 10px; font-weight: 700; }
.login-step__switch button.active { background: #fff; color: rgb(var(--v-theme-primary)); box-shadow: 0 4px 13px rgba(49,38,99,.09); }
.login-step__form { display: grid; gap: 6px; margin-top: 16px; }
.login-step__form--register { gap: 12px; }
.login-step__form :deep(.v-btn) { font-size: 12px; font-weight: 700; }
.login-step__alternate { margin-top: 14px; color: rgba(var(--v-theme-on-surface),.55); font-size: 9px; text-align: center; }
.login-step__alternate button,.login-step__terms button { border: 0; padding: 0; background: transparent; color: rgb(var(--v-theme-primary)); cursor: pointer; font: inherit; font-weight: 700; }
.login-step__terms { margin: 13px 0 0; color: rgba(var(--v-theme-on-surface),.48); font-size: 8px; line-height: 1.9; text-align: center; }
.login-step__terms button { text-decoration: underline; text-decoration-color: rgba(var(--v-theme-primary),.35); text-underline-offset: 3px; }
.login-step__security { display: flex; align-items: center; justify-content: center; gap: 5px; margin-top: 13px; padding-top: 13px; border-top: 1px solid rgba(var(--v-theme-on-surface),.07); color: rgba(var(--v-theme-on-surface),.44); font-size: 8px; }
@media (max-width:380px) { .login-step { padding-inline:18px; } }
</style>
